import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Pencil,
  Share2,
  Mail,
  BarChart3,
  Calendar,
  Briefcase,
  Activity,
  Tag,
  Users,
  ShieldCheck,
  Phone,
  MapPin,
  Clock,
  Flame,
  CheckCircle,
  ListChecks,
  FileText,
  Download,
  User,
  Camera,
  Loader2,
} from 'lucide-react';
import { toast } from '../../utils/toast';
import { API_URL, UPLOAD_URL } from '../../api/api';
import { MEMBER_DOCUMENTS, openMemberDocument } from '../../utils/memberDocuments';
import { parseAlternateEmails } from '../../utils/alternateEmails';
import { skillNames } from '../../utils/skills';
import { hasPermission } from '../../utils/permissionUtils';
import './employeeProfile.css';

const auth = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

// The central envelope in middleware/errorHandler.js sends
// { error: { code, message } }. Dropping the object form straight into a toast
// throws "Objects are not valid as a React child", so always narrow to text.
// Same helper as TeamManagement.js.
const apiErrorMsg = (err, fallback) => {
  const e = err?.response?.data?.error;
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') return e.message || e.code || fallback;
  return fallback;
};

// Uploaded files are served from UPLOAD_URL, not API_URL — on the deployed
// host the backend sits under /backend, so an API_URL-based path falls through
// to the SPA's index.html catch-all and the image never loads. Matches the
// helpers in TeamManagement.js / OrgCard.js.
const photoUrl = (p) => {
  if (!p) return null;
  if (/^https?:\/\//.test(p)) return p;
  if (p.startsWith('data:')) return p;
  return `${UPLOAD_URL}${p.startsWith('/') ? '' : '/'}${p}`;
};

const initialsOf = (name = '') =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || '?';

// Tenure helper — produces "2.3y" / "11mo" / "3w".
const tenureFromJoin = (dateStr) => {
  if (!dateStr) return { short: '—', months: 0, years: 0 };
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return { short: '—', months: 0, years: 0 };
  const now = new Date();
  const months = Math.max(
    0,
    (now.getFullYear() - d.getFullYear()) * 12 +
      (now.getMonth() - d.getMonth())
  );
  const years = months / 12;
  const short =
    months >= 12
      ? `${years.toFixed(1)}y`
      : months >= 1
      ? `${months}mo`
      : `${Math.max(1, Math.round((now - d) / 86400000 / 7))}w`;
  return { short, months, years };
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

// Animated number that counts up when first painted.
const CountUp = ({ to, suffix = '', durationMs = 700 }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const start = performance.now();
    const target = Number(to) || 0;
    let raf;
    const step = (t) => {
      const k = Math.min(1, (t - start) / durationMs);
      const eased = 1 - Math.pow(1 - k, 3);
      setVal(target * eased);
      if (k < 1) raf = requestAnimationFrame(step);
      else setVal(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [to, durationMs]);
  const display =
    Number.isInteger(Number(to)) && Number(to) >= 100
      ? Math.round(val)
      : Number(to) % 1 === 0
      ? Math.round(val)
      : val.toFixed(1);
  return (
    <span>
      {display}
      {suffix}
    </span>
  );
};

// Current month, as the tag beside the "This Month" heading ("September 2026").
// Computed once at module load: the profile is not open across a month
// boundary, and re-deriving it per render would allocate a formatter each time.
const CURRENT_MONTH_LABEL = new Date().toLocaleString('en-US', {
  month: 'long',
  year: 'numeric',
});

// How many days the current month has. Day 0 of the NEXT month is the last day
// of this one, which gets February and leap years right without a table.
const daysInCurrentMonth = () => {
  const n = new Date();
  return new Date(n.getFullYear(), n.getMonth() + 1, 0).getDate();
};

// One attendance status -> one heatmap tile. Same source the Attendance page's
// monthly calendar reads (GET /api/attendance/calendar/:userId), so the two
// screens can't tell different stories about the same day.
//
// A day the API returns NOTHING for gets the neutral tile, and that is the
// whole answer to "before they joined": dbGetCalendar deliberately emits no
// entry for days earlier than the employee's joining date, so someone who
// started on the 3rd has the 1st and 2nd rendered blank rather than counted as
// absences they could not possibly have avoided. Future days are blank for the
// same reason — nothing has happened yet.
const TILE_FOR_STATUS = {
  present: 'ontime',        // upgraded to 'late' below when the punch was late
  'in-progress': 'ontime',
  leave: 'leave',
  absent: 'absent',
  weekoff: 'off',
  holiday: 'holiday',
};

const HEATMAP_KEYS = [
  ['ontime', 'On time'],
  ['late', 'Late'],
  ['leave', 'Leave'],
  ['absent', 'Absent'],
  ['holiday', 'Holiday'],
  ['off', 'Week-off'],
];

// Attendance heatmap for the current month, from the employee's real records.
const AttendanceHeatmap = ({ userId, days }) => {
  const [byDate, setByDate] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    // No login behind this employee record (6 of 62 accounts have none), so
    // there is no attendance to fetch — resolve to the empty state rather than
    // leaving the card on "Loading…" forever.
    if (!userId) {
      setFailed(true);
      return undefined;
    }
    let cancelled = false;
    const now = new Date();
    const month = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    axios
      .get(`${API_URL}/api/attendance/calendar/${userId}`, { ...auth(), params: { month } })
      .then((r) => {
        if (cancelled) return;
        const map = {};
        for (const row of r.data || []) map[String(row.date).slice(0, 10)] = row;
        setByDate(map);
      })
      .catch(() => {
        // A member without rights to another employee's attendance gets a 403
        // here. Show the card empty rather than an error — the profile itself
        // is still perfectly readable.
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const tiles = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const out = [];
    for (let d = 1; d <= days; d++) {
      const row = byDate?.[`${y}-${m}-${String(d).padStart(2, '0')}`];
      let tile = TILE_FOR_STATUS[row?.status] || 'off';
      if (tile === 'ontime' && row?.late) tile = 'late';
      out.push(tile);
    }
    return out;
  }, [byDate, days]);

  // "Working days" counts only the days the employee was actually expected in
  // and that have already happened — on time, late, or absent. Week-offs,
  // holidays, leave and days not yet reached are not working days, so counting
  // them would deflate the ratio for reasons nobody is answerable for.
  const onTimeCount = tiles.filter((t) => t === 'ontime').length;
  const workingDays = tiles.filter((t) => t === 'ontime' || t === 'late' || t === 'absent').length;

  return (
    <>
      <p className="ep-heatmap-summary">
        {byDate == null
          ? failed
            ? 'Attendance not available'
            : 'Loading attendance…'
          : `${onTimeCount}/${workingDays} working days on time`}
      </p>
      <div className="ep-heatmap-grid">
        {tiles.map((t, i) => (
          <span
            key={i}
            className={`ep-heatmap-tile is-${t}`}
            title={`${i + 1} — ${HEATMAP_KEYS.find(([k]) => k === t)?.[1] || t}`}
          />
        ))}
      </div>
      <div className="ep-heatmap-legend">
        {HEATMAP_KEYS.map(([k, label]) => (
          <span key={k}><i className={`ep-legend-dot is-${k}`} /> {label}</span>
        ))}
      </div>
    </>
  );
};

// Shown until the real figures are wired up.
// Tenure is still a placeholder. Kept as the string '0.0' rather than the
// number 0 on purpose: StatTile only animates a real number, and CountUp would
// render 0 as "0", losing the decimal that marks this as unwired.
const STAT_PLACEHOLDER = '0.0';

// A Status figure, or an em dash when there is nothing to report yet — still
// loading, no login behind the employee record, or a 403. Printing 0 for any
// of those would assert "zero hours worked" where the truth is "not known",
// and StatTile animates a real number up from 0 anyway, so a genuine zero is
// still distinguishable from a dash.
const statValue = (n) => (typeof n === 'number' && Number.isFinite(n) ? n : '—');

const StatTile = ({ icon: Icon, value, suffix, label, sub }) => {
  // Only a real number is animated. Strings print verbatim — a composite like
  // "6/22" or "3.8y" becomes NaN in CountUp and renders a misleading "0.0",
  // and a "0.0" placeholder would collapse to "0".
  const animatable = typeof value === 'number' && Number.isFinite(value);
  return (
    <div className="ep-stat-tile">
      <span className="ep-stat-icon">
        <Icon size={13} />
      </span>
      <b className="ep-stat-value">
        {animatable ? (
          <CountUp to={value} suffix={suffix || ''} />
        ) : (
          <>
            {value}
            {suffix || ''}
          </>
        )}
      </b>
      <span className="ep-stat-label">{label}</span>
      {sub && <small className="ep-stat-sub">{sub}</small>}
    </div>
  );
};

// Placeholder progress until the real criteria are agreed — cycled per row so
// the bars read as a list rather than one repeated value. Replace with the
// computed percentage (see the note on the Current projects card).
const DUMMY_PROGRESS = [70, 50, 30];

// No status pill or due date yet: "on track" vs "at risk" needs a rule we
// haven't settled, and inventing one would be worse than leaving it out.
const ProjectRow = ({ name, client, pct }) => (
  <div className="ep-project-row">
    <div className="ep-project-head">
      <span className="ep-project-name">{name}</span>
      {client && <span className="ep-project-client">{client}</span>}
    </div>
    <div className="ep-project-bar-row">
      <div className="ep-project-bar">
        <span className="ep-project-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="ep-project-pct">{pct}%</span>
    </div>
  </div>
);

// Cosmetic-only descriptor per document type — not real file metadata, since
// the backend stores no per-document upload date/size to show honestly.
const DOC_SUBTITLES = {
  resume: 'PDF Document',
  offer_letter: 'Official PDF',
  appointment_letter: 'Signed Document',
  confirmation_letter: 'Signed Document',
  employment_contract: 'Signed Document',
  nda_agreement: 'Signed Document',
  pan_card: 'Identity Proof',
  aadhar_card: 'Identity Proof',
  certificate: 'Certificate',
  edu_certificates: 'Education Record',
  other_edu_certificates: 'Education Record',
  salary_slip: 'Payroll Document',
  reli_experience: 'Experience Letter',
  exit_document: 'HR Verification',
};

const TEAM_AVATAR_COLORS = [
  '#2563eb', '#16a34a', '#9333ea', '#f97316', '#dc2626', '#0891b2',
];
const teamAvatarColor = (id) => TEAM_AVATAR_COLORS[(Number(id) || 0) % TEAM_AVATAR_COLORS.length];

// Matches middleware/upload.js on the server, so the user is told here rather
// than by a 413 after the whole file has been sent.
const MAX_PHOTO_BYTES = 10 * 1024 * 1024;

const TABS = [
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'documents', label: 'Documents', icon: FileText },
  { key: 'timeline', label: 'Timeline', icon: Activity },
];

export default function EmployeeProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const role = (localStorage.getItem('role') || 'admin').toLowerCase();
  const base =
    role === 'hr' ? '/hr' : role === 'member' ? '/member' : '/admin';
  // Edit, documents, photo and emergency contact for anyone's record are
  // `manage_employees` (mirrors allowSelfOrManage on the server). Without it
  // the profile is read-only, apart from the person's own record.
  const canEdit = hasPermission('manage_employees');
  // The logged-in user's own linked employee id (for own-document access).
  const ownMemberId = (() => {
    try {
      return JSON.parse(localStorage.getItem('userdata') || '{}').member_id ?? null;
    } catch {
      return null;
    }
  })();

  const [member, setMember] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [projectsState, setProjectsState] = useState({
    list: [],
    loading: true,
    denied: false,
  });
  // Set when the uploaded photo fails to load, so we fall back to initials.
  const [photoFailed, setPhotoFailed] = useState(false);
  // Local object URL shown while a newly picked photo is uploading.
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [memberRes, listRes] = await Promise.all([
          axios.get(`${API_URL}/api/team/${id}`, auth()),
          axios
            .get(`${API_URL}/api/org-chart/employees`, auth())
            .catch(() => ({ data: { employees: [] } })),
        ]);
        if (cancelled) return;
        setMember(memberRes.data);
        setEmployees(listRes.data?.employees || []);
      } catch (err) {
        if (err?.response?.status === 401) {
          localStorage.clear();
          window.location.assign('/login');
          return;
        }
        if (!cancelled)
          setError(err?.response?.data?.error || 'Could not load employee');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Projects this employee is a member of. Best-effort and separate from the
  // main load: a 403 (a member viewing a colleague) or a workspace error must
  // leave the rest of the profile intact.
  useEffect(() => {
    let cancelled = false;
    setProjectsState({ list: [], loading: true, denied: false });
    axios
      .get(`${API_URL}/api/workspace/projects?member_id=${id}`, auth())
      .then((r) => {
        if (cancelled) return;
        setProjectsState({
          list: Array.isArray(r.data) ? r.data : [],
          loading: false,
          denied: false,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setProjectsState({
          list: [],
          loading: false,
          denied: err?.response?.status === 403,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  // Derived data — runs whether `member` is loaded or not (returns sane
  // fallbacks). Hooks must always run in the same order.
  const fullName = member
    ? `${member.first_name || ''} ${member.last_name || ''}`.trim()
    : '';
  // `/api/team/:id` returns `jobtitle` as a numeric FK. The org-chart employees
  // list (fetched alongside) carries the resolved job-title name, so prefer
  // that; fall back to any free-text designation, never the bare numeric id.
  const selfShaped = useMemo(
    () => employees.find((e) => e.id === member?.id) || null,
    [employees, member?.id]
  );
  const designation =
    selfShaped?.designation ||
    (member && !/^\d+$/.test(String(member.jobtitle || '')) ? member.jobtitle : '') ||
    member?.designation ||
    '';
  const department =
    selfShaped?.department ||
    (member && !/^\d+$/.test(String(member.department || '')) ? member.department : '') ||
    '';
  const tenure = useMemo(
    () => tenureFromJoin(member?.join_date || member?.doj),
    [member]
  );

  // The Status card's six figures, for the current calendar year. One request
  // because the tiles render as a unit and five of the six share the year
  // window — fetching them piecemeal would let the tiles disagree about where
  // the year ends. `null` while loading or unavailable, so the tiles can show
  // a dash instead of asserting a real zero.
  const [status, setStatus] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    setStatus(null);
    axios
      .get(`${API_URL}/api/team/${id}/status`, auth())
      .then((r) => {
        if (!cancelled) setStatus(r.data || null);
      })
      .catch(() => {
        // 403 for a member viewing a colleague (the route is gated
        // self-or-manager, like the timeline). The rest of the profile is
        // still fine, so the tiles just show dashes.
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const photo = photoUrl(member?.profile_photo);
  // Navigating between employees must clear a previous load failure.
  useEffect(() => setPhotoFailed(false), [photo]);
  // Release the previous object URL whenever it is replaced or we unmount.
  useEffect(
    () => () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    },
    [photoPreview]
  );

  // Skills — the column holds the structured list (or, on older rows, a plain
  // comma-separated string); only the names are shown as tags here.
  const skills = useMemo(() => skillNames(member?.skills), [member?.skills]);

  // Manager + direct reports from the org-chart employees list.
  const managerInfo = useMemo(() => {
    if (!member?.primary_manager_id) return null;
    return employees.find((e) => e.id === member.primary_manager_id) || null;
  }, [employees, member?.primary_manager_id]);

  const directReports = useMemo(() => {
    if (!member) return [];
    return employees
      .filter((e) => e.primary_manager_id === member.id)
      .slice(0, 8);
  }, [employees, member]);

  const docs = useMemo(() => {
    if (!member) return [];
    return MEMBER_DOCUMENTS.filter((d) => member[d.field]);
  }, [member]);

  const isOwnRecord =
    ownMemberId != null && Number(ownMemberId) === Number(member?.id);
  const canSeeDocs = canEdit || isOwnRecord;
  // Mirrors allowSelfOrManage on PATCH /api/team/:id/photo.
  const canChangePhoto = canEdit || isOwnRecord;

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    // Clear the input so re-picking the same file after a failure still fires.
    e.target.value = '';
    if (!file) return;
    if (!/\.(jpe?g|png)$/i.test(file.name)) {
      toast.error('Profile photo must be a JPG or PNG image.');
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error('That photo is over the 10 MB limit — pick a smaller one.');
      return;
    }

    // Show the picked image straight away; revert it if the upload fails.
    const localUrl = URL.createObjectURL(file);
    setPhotoPreview(localUrl);
    setPhotoFailed(false);
    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append('profile_photo', file);
      const res = await axios.patch(
        `${API_URL}/api/team/${id}/photo`,
        fd,
        auth()
      );
      const saved = res.data?.profile_photo;
      if (saved) {
        setMember((m) => (m ? { ...m, profile_photo: saved } : m));
        // Drop the preview so what's on screen is the stored file.
        setPhotoPreview(null);
        // Keep the header avatar in step when it's the signed-in user's photo.
        if (isOwnRecord) {
          try {
            const ud = JSON.parse(localStorage.getItem('userdata') || '{}');
            ud.profile_photo = saved;
            localStorage.setItem('userdata', JSON.stringify(ud));
            window.dispatchEvent(new Event('userdataChanged'));
          } catch {
            /* header will catch up on next load */
          }
        }
      }
      toast.success('Profile photo updated.');
    } catch (err) {
      setPhotoPreview(null);
      toast.error(apiErrorMsg(err, 'Could not update the photo.'));
    } finally {
      setPhotoUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="ep-page">
        <p style={{ padding: 32 }}>Loading profile…</p>
      </div>
    );
  }
  if (error || !member) {
    return (
      <div className="ep-page">
        <p style={{ padding: 32, color: '#dc2626' }}>
          ⚠ {error || 'Employee not found'}
        </p>
        <Link to={`${base}/teams`}>← Back to employees</Link>
      </div>
    );
  }

  const firstName = fullName.split(' ')[0] || 'them';

  return (
    <div className="ep-page">
      <div className="ep-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const count =
            t.key === 'documents' && canSeeDocs ? ` (${docs.length})` : '';
          return (
            <button
              key={t.key}
              type="button"
              className={`ep-tab ${activeTab === t.key ? 'is-active' : ''}`}
              onClick={() => setActiveTab(t.key)}
            >
              <Icon size={14} /> {t.label}{count}
            </button>
          );
        })}
      </div>

      {activeTab === 'profile' && (
        <>
          <section className="ep-columns">
            {/* ROW 1 — photo card | personal & work info | emergency + last 30
                days. All three are grid items in the same row, so they stretch
                to a common height and share one bottom edge. */}
            <div className="ep-photo-card">
              <div className="ep-photo-bg">
                {photoPreview || (photo && !photoFailed) ? (
                  <img
                    className="ep-photo-img"
                    src={photoPreview || photo}
                    alt={fullName || 'Profile photo'}
                    onError={() => setPhotoFailed(true)}
                  />
                ) : (
                  <span className="ep-photo-initials">{initialsOf(fullName)}</span>
                )}
              </div>
              <div className="ep-photo-top">
                <button
                  type="button"
                  className="ep-photo-btn"
                  onClick={() => navigate(`${base}/teams`)}
                  title="Back to employees"
                  aria-label="Back to employees"
                >
                  <ArrowLeft size={14} />
                </button>
                <div className="ep-photo-actions">
                  {canChangePhoto && (
                    <>
                      <input
                        ref={photoInputRef}
                        type="file"
                        accept="image/jpeg,image/png"
                        onChange={onPickPhoto}
                        hidden
                      />
                      <button
                        type="button"
                        className="ep-photo-btn"
                        onClick={() => photoInputRef.current?.click()}
                        disabled={photoUploading}
                        title="Change photo"
                        aria-label="Change profile photo"
                      >
                        {photoUploading ? (
                          <Loader2 size={14} className="ep-spin" />
                        ) : (
                          <Camera size={14} />
                        )}
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    className="ep-photo-btn"
                    onClick={() => {
                      const url = window.location.href;
                      if (navigator.share) {
                        navigator.share({ title: fullName, url }).catch(() => {});
                      } else {
                        navigator.clipboard?.writeText(url);
                      }
                    }}
                    title="Share profile"
                    aria-label="Share profile"
                  >
                    <Share2 size={14} />
                  </button>
                  {canEdit && (
                    <button
                      type="button"
                      className="ep-photo-edit"
                      onClick={() => navigate(`${base}/teams/${id}/edit`)}
                    >
                      <Pencil size={12} /> Edit
                    </button>
                  )}
                </div>
              </div>
              <div className="ep-photo-overlay">
                <h1>{fullName || 'Unnamed'}</h1>
                <span className="ep-photo-role">{designation || 'Team member'}</span>
              </div>
            </div>

            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><ShieldCheck size={14} /></span>
                Personal &amp; work info
              </h3>
              <dl className="ep-info-grid">
                <div className="ep-info-item">
                  <dt>Employee ID</dt>
                  <dd>EM-{String(member.id).padStart(4, '0')}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Work Shift</dt>
                  {/* work_shift is the shifts.id; work_shift_name is resolved
                      server-side. Fall back to the raw value so an older row
                      that stored a name still reads correctly. */}
                  <dd>{member.work_shift_name || member.work_shift || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Date of Joining</dt>
                  <dd>{fmtDate(member.join_date || member.doj)}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Tenure</dt>
                  <dd>{tenure.short}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Date of Birth</dt>
                  <dd>{fmtDate(member.dob)}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Gender</dt>
                  <dd>{member.gender || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Nationality</dt>
                  <dd>{member.nationality || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Department</dt>
                  <dd>{department || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Blood Group</dt>
                  <dd>{member.blood_group || '—'}</dd>
                </div>
                <div className="ep-info-item">
                  <dt>Address</dt>
                  <dd>{member.address || '—'}</dd>
                </div>
              </dl>
              <div className="ep-info-rows">
                <div className="ep-info-row">
                  <span className="ep-info-row-label"><Mail size={12} /> Email</span>
                  <span className="ep-info-row-value">
                    <a href={`mailto:${member.email}`}>{member.email}</a>
                  </span>
                </div>
                {parseAlternateEmails(member.alternate_emails).map((alt) => (
                  <div className="ep-info-row" key={alt}>
                    <span className="ep-info-row-label"><Mail size={12} /> Alternate</span>
                    <span className="ep-info-row-value">
                      <a href={`mailto:${alt}`}>{alt}</a>
                    </span>
                  </div>
                ))}
                {member.contact_number && (
                  <div className="ep-info-row">
                    <span className="ep-info-row-label"><Phone size={12} /> Phone</span>
                    <span className="ep-info-row-value">{member.contact_number}</span>
                  </div>
                )}
                {member.work_location && (
                  <div className="ep-info-row">
                    <span className="ep-info-row-label"><MapPin size={12} /> Location</span>
                    <span className="ep-info-row-value">{member.work_location}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="ep-side-stack">
              {/* `manage_employees` only. GET /api/team/:id also strips these three
                  fields for anyone else, so this is the presentation half of
                  the rule, not the enforcement — a member who reached the page
                  would find nothing to read even if the card rendered. */}
              {canEdit && (
                <div className="ep-card ep-card-warm">
                  <h3 className="ep-card-head">
                    <span className="ep-card-icon is-danger"><Phone size={14} /></span>
                    Emergency contact details
                  </h3>
                  <div className="ep-emergency-rows">
                    <div className="ep-emergency-row">
                      <span className="ep-emergency-label"><User size={12} /> Name</span>
                      <span className="ep-emergency-value">
                        {member.emergency_contact_name || '—'}
                      </span>
                    </div>
                    <div className="ep-emergency-row">
                      <span className="ep-emergency-label"><Phone size={12} /> Phone</span>
                      <span className="ep-emergency-value">
                        {member.emergency_contact_phone || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className="ep-card">
                <h3 className="ep-card-head">
                  <span className="ep-card-icon"><Calendar size={14} /></span>
                  This Month
                  <span className="ep-pill ep-pill-month">{CURRENT_MONTH_LABEL}</span>
                </h3>
                {/* Days in the current month, not a rolling 30 — the heading
                    names a calendar month, so the grid has to hold exactly
                    that month's days (28 in February, 31 in March).
                    `user_id` is the login behind this employee record; the
                    attendance API keys on users.id, not team_members.id. */}
                <AttendanceHeatmap userId={member.user_id} days={daysInCurrentMonth()} />
              </div>
            </div>

            {/* ROW 2 — status | skills & tags | team */}
            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><BarChart3 size={14} /></span>
                Status
              </h3>
              {/* Year-to-date, from GET /api/team/:id/status. `statValue`
                  turns a null (still loading, no login, or a 403) into a dash
                  rather than a real-looking 0.

                  No `sub` captions: the tiles are only ~100px wide, so a
                  caption wrapped onto two or three lines and pushed the grid
                  out of alignment. The window and the on-time division are
                  documented on the endpoint instead. */}
              <div className="ep-stat-grid">
                <StatTile
                  icon={Clock}
                  value={statValue(status?.total_hours)}
                  suffix={status?.total_hours != null ? 'h' : ''}
                  label="Total Hours"
                />
                <StatTile
                  icon={Calendar}
                  value={statValue(status?.leaves_used)}
                  label="Leaves used"
                />
                <StatTile
                  icon={Flame}
                  value={statValue(status?.streak_current)}
                  label="Day streak"
                />
                <StatTile
                  icon={CheckCircle}
                  value={statValue(status?.on_time_rate)}
                  suffix={status?.on_time_rate != null ? '%' : ''}
                  label="On-time rate"
                />
                {/* Left as a placeholder on request. `tenure` is already
                    computed and shown in Personal & work info, so switching
                    this to `tenure.short` is the only change needed. */}
                <StatTile icon={Briefcase} value={STAT_PLACEHOLDER} label="Tenure" />
                <StatTile
                  icon={ListChecks}
                  value={statValue(status?.tasks_done)}
                  label="Tasks done"
                />
              </div>
            </div>

            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><Tag size={14} /></span>
                Skills &amp; tags
              </h3>
              {skills.length === 0 ? (
                <p className="ep-muted">No skills recorded yet.</p>
              ) : (
                <div className="ep-tag-cloud">
                  {skills.map((s) => (
                    <span key={s} className="ep-tag-pill">
                      @{s.replace(/^@/, '').replace(/\s+/g, '-').toLowerCase()}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="ep-card">
              <h3 className="ep-card-head">
                <span className="ep-card-icon"><Users size={14} /></span>
                Team
              </h3>
              <div className="ep-team-label">REPORTS TO</div>
              {managerInfo ? (
                <Link to={`${base}/teams/${managerInfo.id}`} className="ep-team-row">
                  <span
                    className="ep-team-avatar"
                    style={{ background: teamAvatarColor(managerInfo.id) }}
                  >
                    {initialsOf(managerInfo.name)}
                  </span>
                  <div>
                    <div className="ep-team-name">{managerInfo.name}</div>
                    <div className="ep-team-sub">
                      {managerInfo.designation || 'Manager'}
                    </div>
                  </div>
                </Link>
              ) : (
                <p className="ep-muted">No manager assigned.</p>
              )}
              {directReports.length > 0 && (
                <>
                  <div className="ep-team-label">DIRECT REPORTS · {directReports.length}</div>
                  <div className="ep-team-list">
                    {directReports.map((r) => (
                      <Link key={r.id} to={`${base}/teams/${r.id}`} className="ep-team-row">
                        <span
                          className="ep-team-avatar"
                          style={{ background: teamAvatarColor(r.id) }}
                        >
                          {initialsOf(r.name)}
                        </span>
                        <div>
                          <div className="ep-team-name">{r.name}</div>
                          <div className="ep-team-sub">
                            {r.designation || `Reports to ${firstName}`}
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>

          <section className="ep-card ep-projects-full">
            <h3 className="ep-card-head">
              <span className="ep-card-icon"><Briefcase size={14} /></span>
              Current projects
              {projectsState.list.length > 0 && (
                <span className="ep-project-count">{projectsState.list.length}</span>
              )}
            </h3>
            {projectsState.loading ? (
              <p className="ep-muted">Loading projects…</p>
            ) : projectsState.denied ? (
              <p className="ep-muted">
                Only Admin and HR can see another employee&apos;s projects.
              </p>
            ) : projectsState.list.length === 0 ? (
              <p className="ep-muted">
                {firstName} is not assigned to any projects.
              </p>
            ) : (
              <>
                {projectsState.list.map((p, i) => (
                  <ProjectRow
                    key={p.id}
                    name={p.name}
                    client={p.client_name}
                    pct={DUMMY_PROGRESS[i % DUMMY_PROGRESS.length]}
                  />
                ))}
                <p className="ep-project-note">
                  Project names are live; the progress figures are placeholders.
                </p>
              </>
            )}
          </section>
        </>
      )}

      {activeTab === 'documents' && (
        <section>
          {!canSeeDocs ? (
            <p className="ep-muted">You do not have access to this employee's documents.</p>
          ) : (
            <>
              <h2 className="ep-doc-heading">Verified Documents ({docs.length})</h2>
              {docs.length === 0 ? (
                <p className="ep-muted">No documents on file.</p>
              ) : (
                <div className="ep-doc-grid">
                  {docs.map((d) => (
                    <div key={d.field} className="ep-doc-card">
                      <span className="ep-doc-icon"><FileText size={18} /></span>
                      <div className="ep-doc-body">
                        <div className="ep-doc-title">{d.label}</div>
                        <div className="ep-doc-sub">{DOC_SUBTITLES[d.field] || 'Document'}</div>
                      </div>
                      <button
                        type="button"
                        className="ep-doc-download"
                        title={`Download ${d.label}`}
                        aria-label={`Download ${d.label}`}
                        onClick={() =>
                          openMemberDocument(member.id, d.field).catch((err) =>
                            toast.error(
                              err?.response?.status === 403
                                ? 'You do not have access to this document.'
                                : err?.response?.status === 404
                                  ? 'The document file is missing.'
                                  : apiErrorMsg(err, 'Could not open the document.'),
                            ),
                          )
                        }
                      >
                        <Download size={15} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeTab === 'timeline' && (
        <section className="ep-card">
          <div className="ep-soon">
            <span className="ep-soon-icon"><Activity size={20} /></span>
            <h3>Coming soon</h3>
            <p>Employee timeline &mdash; joins, leaves, letters and milestones &mdash; is on its way.</p>
          </div>
        </section>
      )}
    </div>
  );
}
